/**
 * S-20 «Действия», S-21 «Карточка действия», S-22 «История решений».
 *
 * Очередь — предложения агентов из утренних отчётов. Штаб кабинет WB не меняет: действие
 * делается в кабинете, а здесь владелец отмечает «Сделано» или «Не буду» с причиной —
 * так очередь не копится, а причины отказов подстраивают агентов. Сотрудник видит очередь,
 * отмечает — владелец ([SEC-11.3.2]).
 */
import { useState } from 'react'
import { fetchHistory, type ActionItem } from '../api/client.js'
import { decideAction, fetchAction, fetchActionQueue, setActionVerdict } from '../api/sections.js'
import { copyText, hapticSuccess } from '../telegram/call.js'
import { Icon } from '../components/icons/Icon.js'
import { Empty, Failed, Loading, NoShop, useAnnounce, useData, useScreenHead } from './common.js'
import { Kv, Sect, ToneTag, VerdictRow, lowerFirst } from './ui.js'
import { agentIcon, agentTitle, decisionWord, REVERSIBILITY } from './words.js'
import type { ScreenId } from '../router/screens.js'
import artActions from '../assets/illustrations/actions.webp'
import artLog from '../assets/illustrations/log.webp'

type Open = (id: ScreenId, param?: string) => void
type State = 'pending' | 'done' | 'rejected'

/** «Сделано в кабинете WB · 13 октября», «Отклонено: сделаю позже · 13 октября», «Устарело». */
export function decisionLine(a: ActionItem): string {
  const word = a.decision === 'confirmed' && a.decision_note ? a.decision_note.charAt(0).toUpperCase() + a.decision_note.slice(1)
    : a.decision === 'rejected' && a.decision_note ? `Не буду: ${a.decision_note}` : decisionWord(a.decision)
  return word + (a.decided_display ? ` · ${a.decided_display}` : '')
}

function Row({ a, onOpen, line }: { a: ActionItem; onOpen: () => void; line: string }): React.JSX.Element {
  return (
    <button type="button" className="row row-tap has-ic" data-testid={'action-' + a.id} onClick={onOpen}>
      <Icon name={agentIcon(a.agent)} className="ic-muted" />
      <span className="row-main">
        <span className="row-title">{a.title !== undefined && a.title !== '' ? a.title : 'Предложение агента'}</span>
        <span className="row-meta">{line}</span>
      </span>
      <Icon name="nav-next" className="ic-chev row-end" />
    </button>
  )
}

export function ScreenActions({ open }: { open: Open }): React.JSX.Element {
  const [state, setState] = useState<State>('pending')
  const { s, reload } = useData(() => fetchActionQueue(state), [state])
  const pending = s.kind === 'ok' && s.data.shop !== null ? s.data.pending_pcs : null
  useScreenHead(undefined, pending !== null && pending > 0 ? `Ждут решения: ${pending}` : null)
  const seg = (v: State, label: string): React.JSX.Element => (
    <button type="button" className={'seg' + (state === v ? ' seg-on' : '')} aria-pressed={state === v} data-testid={'act-' + v} onClick={() => setState(v)}>
      {label}{v === 'pending' && pending !== null && <span className="seg-n">{pending}</span>}
    </button>
  )
  return (
    <div data-testid="screen-body">
      <div className="segs" role="group" aria-label="Какие действия показать">
        {seg('pending', 'Ждут')}{seg('done', 'Сделано')}{seg('rejected', 'Не буду')}
      </div>
      {s.kind === 'loading' && <Loading label="Загрузка действий" />}
      {s.kind === 'fail' && <Failed message={s.message} onRetry={reload} />}
      {s.kind === 'ok' && (s.data.shop === null ? <NoShop reason={s.data.reason} /> : s.data.items.length === 0
        ? (state === 'pending'
            ? <Empty art={artActions} title="Всё спокойно" testid="empty"
                text="Решений не ждёт ничего. Новые предложения агенты присылают утром — они появятся здесь." />
            : <Empty art={artLog} title={state === 'done' ? 'Сделанного пока нет' : 'Отказов пока нет'} testid="empty"
                text="Здесь появятся предложения, по которым вы отметили решение." />)
        : <section className="card gap-top" data-testid="action-list">
            {s.data.items.map((a) => (
              <Row key={a.id} a={a} onOpen={() => open('S-21', a.id)}
                line={state === 'pending'
                  ? [agentTitle(a.agent), a.date_display, REVERSIBILITY[a.reversibility]].filter(Boolean).join(' · ')
                  : `${agentTitle(a.agent)} · ${decisionLine(a)}`} />
            ))}
          </section>)}
      <section className="card gap-top">
        <button type="button" className="row row-tap row-link has-ic" data-testid="history-link" onClick={() => open('S-22')}>
          <Icon name="section-history" className="ic-action" />
          <span className="row-main"><span className="row-title">История решений</span></span>
          <Icon name="nav-next" className="ic-chev row-end" />
        </button>
      </section>
    </div>
  )
}

/** Что значит обратимость — одной строкой (ТЗ S-21; 04-voice, 2.5). */
const REV_EXPLAIN: Record<string, string> = {
  R1: 'Можно отменить: изменение возвращается, например ставка.',
  R2: 'Можно отменить частично: вернуть можно, но последствия могут остаться.',
  R3: 'Отменить нельзя: например, опубликованный ответ покупателю.',
}

export function ScreenAction({ id, open }: { id: string; open: Open }): React.JSX.Element {
  const { s, reload } = useData(() => fetchAction(id))
  const a = s.kind === 'ok' && s.data.shop !== null ? s.data : null
  useScreenHead(a === null ? undefined : agentTitle(a.agent), a === null ? null : `${a.date_display ?? ''} · ${decisionLine(a)}`)
  const announce = useAnnounce()
  const [busy, setBusy] = useState(false)
  const [rejecting, setRejecting] = useState(false)
  if (s.kind === 'loading') return <Loading label="Загрузка действия" rows={5} />
  if (s.kind === 'fail') return <Failed message={s.message} onRetry={reload} />
  if (a === null) return <NoShop reason={s.data.reason} />
  const decide = async (decision: 'done' | 'rejected', note?: string): Promise<void> => {
    setBusy(true)
    const r = await decideAction(a.id, decision, note)
    setBusy(false)
    if (!r.ok) { announce({ kind: 'fail', text: r.error.message }); return }
    hapticSuccess()
    announce({ kind: 'ok', text: decision === 'done' ? 'Отмечено: сделано' : 'Отмечено: не будем делать' })
    setRejecting(false)
    reload()
  }
  return (
    <div data-testid="screen-body">
      <section className="card card-pad action-card">
        <p className="action-title" data-testid="action-title">{a.title}</p>
        {a.effect != null && <p className="body-text">Что даст: {lowerFirst(a.effect)}</p>}
        {a.params.length > 0 && <Kv rows={a.params.map((p) => ({ label: p.label, text: p.display }))} />}
        <p className="row-meta"><ToneTag tone={a.reversibility === 'R3' ? 'attn' : 'neutral'} word={REVERSIBILITY[a.reversibility] ?? 'Обратимость неизвестна'} /></p>
        <p className="row-meta">{REV_EXPLAIN[a.reversibility] ?? ''}</p>
      </section>

      {a.answer_text !== null && <>
        <Sect title="Черновик ответа" />
        <section className="card card-pad">
          <p className="body-text pre-wrap" data-testid="action-answer">{a.answer_text}</p>
          <button type="button" className="btn btn-soft btn-block gap-top-s" data-testid="action-copy"
            onClick={() => { void copyText(a.answer_text!).then((ok) => { if (ok) { hapticSuccess(); announce({ kind: 'ok', text: 'Ответ скопирован — вставьте его в кабинете WB' }) } }) }}>
            <Icon name="action-copy" size={20} />Скопировать ответ
          </button>
        </section>
      </>}

      {(a.stop_condition !== null || a.undo !== null) && <>
        <Sect title="Если что-то пойдёт не так" />
        <section className="card">
          <Kv rows={[
            a.stop_condition !== null && { label: 'Когда остановиться', bare: true, sub: a.stop_condition },
            a.undo !== null && { label: 'Как вернуть', bare: true, sub: a.undo },
          ]} />
        </section>
      </>}

      <section className="card gap-top">
        {a.target_kind === 'campaign' && (
          <button type="button" className="row row-tap row-link has-ic" data-testid="action-campaign" onClick={() => open('S-14', a.target_id ?? '')}>
            <Icon name="agent-ads" className="ic-action" />
            <span className="row-main"><span className="row-title">Открыть кампанию</span></span>
            <Icon name="nav-next" className="ic-chev row-end" />
          </button>
        )}
        {(a.target_kind === 'feedback' || a.target_kind === 'question') && (
          <button type="button" className="row row-tap row-link has-ic" data-testid="action-feedback"
            onClick={() => open('S-19', (a.target_kind === 'question' ? 'q-' : '') + (a.target_id ?? ''))}>
            <Icon name="agent-feedback" className="ic-action" />
            <span className="row-main"><span className="row-title">{a.target_kind === 'question' ? 'Открыть вопрос' : 'Открыть отзыв'}</span></span>
            <Icon name="nav-next" className="ic-chev row-end" />
          </button>
        )}
        {a.nm_ids[0] !== undefined && (
          <button type="button" className="row row-tap row-link has-ic" data-testid="action-product" onClick={() => open('S-12', String(a.nm_ids[0]))}>
            <Icon name="tab-products" className="ic-action" />
            <span className="row-main"><span className="row-title">Открыть товар</span></span>
            <Icon name="nav-next" className="ic-chev row-end" />
          </button>
        )}
      </section>

      {a.decision === 'pending' && (
        <section className="card card-pad gap-top" data-testid="action-decide">
          <p className="body-text muted">Сделать это можно в кабинете WB: Штаб читает данные кабинета и сам ничего в нём не меняет. Отметьте решение — очередь не будет копиться.</p>
          {a.can_decide
            ? rejecting
              ? <div className="reasons" role="group" aria-label="Почему не будете делать">
                  {a.reject_reasons.map((r) => (
                    <button type="button" className="seg" key={r} disabled={busy} data-testid={'reason-' + r} onClick={() => { void decide('rejected', r) }}>{r}</button>
                  ))}
                  <button type="button" className="link-inline" onClick={() => setRejecting(false)}>Отмена</button>
                </div>
              : <div className="decide-btns">
                  <button type="button" className="btn btn-block" disabled={busy} data-testid="action-done" onClick={() => { void decide('done') }}>
                    <Icon name="action-confirm" size={20} />Сделано в кабинете
                  </button>
                  <button type="button" className="btn btn-soft btn-block" disabled={busy} data-testid="action-reject" onClick={() => setRejecting(true)}>
                    Не буду делать
                  </button>
                </div>
            : <p className="row-meta">Решение отмечает владелец.</p>}
        </section>
      )}

      <section className="card card-pad gap-top">
        <VerdictRow key={a.id} value={a.my_verdict ?? null} testid="action-verdict" q="Предложение полезное?"
          onSet={async (v) => {
            const x = await setActionVerdict(a.id, v)
            if (!x.ok) announce({ kind: 'fail', text: x.error.message })
            return x.ok
          }} />
      </section>
    </div>
  )
}

export function ScreenHistory({ open }: { open: Open }): React.JSX.Element {
  const { s, reload } = useData(fetchHistory)
  useScreenHead(undefined, null)
  if (s.kind === 'loading') return <Loading label="Загрузка истории решений" />
  if (s.kind === 'fail') return <Failed message={s.message} onRetry={reload} />
  const d = s.data
  if (d.shop === null) return <NoShop reason={d.reason} />
  if (d.items.length === 0) {
    return <Empty art={artLog} title="Решений пока не было" testid="empty"
      text="Здесь появятся предложения агентов, по которым вы отметили «сделано» или «не буду»." />
  }
  return (
    <section className="card" data-testid="screen-body">
      {d.items.map((a) => <Row key={a.id} a={a} onOpen={() => open('S-21', a.id)} line={`${agentTitle(a.agent)} · ${decisionLine(a)}`} />)}
    </section>
  )
}
